import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Grid, Tab, Tabs, Typography } from "@mui/material";
import * as XLSX from "xlsx";
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import TablePagination from "../../common/tablePagination";
import GreenButton from "../../common/button/greenButton";
import { getJobApi, listJobDeliveryApi, listJobRowsApi, listJobSmsDeliveryApi, listJobWhatsappDeliveryApi, pollSmsDeliveryApi, errorMessage } from "./api";
import { CountTile, EmailTable, StatusChip, formatDateTime, useFeedback } from "./common";
import { audienceText, channelLabel } from "./JobList";

const PAGE_SIZE = 20;
const REFRESH_MS = 5000;
const EXPORT_LIMIT = 1000;

export default function JobDetail() {
  const navigate = useNavigate();
  const feedback = useFeedback();
  const [searchParams] = useSearchParams();
  const project = Cookies.get("mhet_mst_project");
  const jobid = searchParams.get("jobid");

  const [job, setJob] = useState(null);
  const [tab, setTab] = useState("rows");
  const [status, setStatus] = useState("");
  const [rowsPage, setRowsPage] = useState(1);
  const [rows, setRows] = useState({ total: 0, rows: [] });
  const [deliveryEvent, setDeliveryEvent] = useState("");
  const [deliveryPage, setDeliveryPage] = useState(1);
  const [delivery, setDelivery] = useState({ total: 0, rows: [] });
  const [error, setError] = useState("");
  const timer = useRef(null);

  const loadJob = async () => {
    try {
      setJob(await getJobApi(jobid));
    } catch (err) {
      setError(errorMessage(err, "Could not load the job"));
    }
  };
  const loadRows = async () => {
    try {
      setRows(await listJobRowsApi(jobid, { status: status || undefined, limit: PAGE_SIZE, offset: (rowsPage - 1) * PAGE_SIZE }));
    } catch (err) {
      feedback.failed(errorMessage(err, "Could not load the rows"));
    }
  };
  // Email: SendGrid events; SMS: the SMS gateway's delivery reports; WhatsApp: the provider's statuses (latest of each message).
  const loadDelivery = async () => {
    const paging = { limit: PAGE_SIZE, offset: (deliveryPage - 1) * PAGE_SIZE };
    try {
      setDelivery(job?.channel === "SMS"
        ? await listJobSmsDeliveryApi(jobid, { status: deliveryEvent || undefined, ...paging })
        : job?.channel === "WHATSAPP"
          ? await listJobWhatsappDeliveryApi(jobid, { status: deliveryEvent || undefined, ...paging })
          : await listJobDeliveryApi(jobid, { event: deliveryEvent || undefined, ...paging }));
    } catch (err) {
      feedback.failed(errorMessage(err, "Could not load delivery status"));
    }
  };

  const fetchSmsDelivery = async () => {
    feedback.loader(true);
    try {
      const res = await pollSmsDeliveryApi();
      feedback.success(`Delivery reports fetched: ${res.data?.saved ?? 0} new`);
      await loadDelivery();
    } catch (err) {
      feedback.failed(errorMessage(err, "Could not fetch delivery reports"));
    }
    feedback.loader(false);
  };

  useEffect(() => {
    loadJob();
    // eslint-disable-next-line
  }, [jobid]);
  useEffect(() => {
    loadRows();
    // eslint-disable-next-line
  }, [jobid, status, rowsPage]);
  useEffect(() => {
    if (tab === "delivery" && ["EMAIL", "SMS"].includes(job?.channel)) loadDelivery();
    // eslint-disable-next-line
  }, [jobid, tab, deliveryEvent, deliveryPage, job?.channel]);

  // While sending, refresh the counts and rows.
  useEffect(() => {
    clearTimeout(timer.current);
    if (job?.status === "RUNNING") {
      timer.current = setTimeout(() => {
        loadJob();
        loadRows();
      }, REFRESH_MS);
    }
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line
  }, [job]);

  const exportRows = async () => {
    feedback.loader(true);
    try {
      const all = [];
      for (let offset = 0; ; offset += EXPORT_LIMIT) {
        const page = await listJobRowsApi(jobid, { status: status || undefined, limit: EXPORT_LIMIT, offset });
        all.push(...page.rows);
        if (all.length >= page.total || page.rows.length === 0) break;
      }
      const sheet = XLSX.utils.json_to_sheet(all.map((r) => ({
        Row: r.rownumber, "Candidate ID": r.candidateid, "Course ID": r.courseid, Contact: r.email || r.mobile || "", Status: r.status, Reason: r.reason || "", "Message ID": r.messageid || "",
      })));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Rows");
      XLSX.writeFile(book, `${String(job?.channel || "send").toLowerCase()}_job_${jobid}${status ? `_${status.toLowerCase()}` : ""}.xlsx`);
    } catch (err) {
      feedback.failed(errorMessage(err, "Could not download the rows"));
    }
    feedback.loader(false);
  };

  if (error) return <Alert severity="error">{error}</Alert>;
  if (!job) return null;

  const isEmail = job.channel === "EMAIL";
  const rowColumns = [
    { name: "Row", accessor: "rownumber" },
    { name: "Candidate ID", accessor: "candidateid" },
    { name: "Course ID", accessor: "courseid" },
    { name: isEmail ? "Email" : "Mobile", align: "left", render: (r) => (isEmail ? r.email : r.mobile) || "—" },
    { name: "Status", render: (r) => <StatusChip status={r.status} /> },
    { name: "Reason", align: "left", render: (r) => r.reason || "—" },
  ];
  const isSms = job.channel === "SMS";
  const isWhatsapp = job.channel === "WHATSAPP";
  // WhatsApp delivery is tracked for jobs sent from the update that added it; older jobs show only Sent / Failed.
  const tracksWhatsapp = isWhatsapp && job.linkwhatsappstatus;
  // SMS / WhatsApp: "Sent" means the provider accepted it; the delivery status comes later.
  if (isSms || tracksWhatsapp) {
    rowColumns.push({
      name: "Delivery",
      align: "left",
      render: (r) => (r.deliverystatus
        ? <Box><StatusChip status={r.deliverystatus} />{r.deliveryreason && <Typography variant="caption" sx={{ display: "block", color: "#a33" }}>{r.deliveryreason}</Typography>}</Box>
        : r.status === "SENT" ? <Typography variant="caption" sx={{ color: "#777" }}>Waiting for status</Typography> : "—"),
    });
  }
  const hasDelivery = isEmail || isSms || tracksWhatsapp;
  const deliveryColumns = isWhatsapp
    ? [
      { name: "Mobile", accessor: "recipient", align: "left" },
      { name: "Candidate ID", accessor: "candidateid" },
      { name: "Latest Status", render: (r) => <StatusChip status={r.status} /> },
      { name: "When", render: (r) => formatDateTime(r.eventat || r.createddate) },
      { name: "Details", align: "left", render: (r) => [r.errorcode && `Code ${r.errorcode}`, r.errormessage].filter(Boolean).join(" · ") || "—" },
    ]
    : isSms
    ? [
      { name: "Mobile", accessor: "mobile", align: "left" },
      { name: "Candidate ID", accessor: "candidateid" },
      { name: "Latest Status", render: (r) => <StatusChip status={r.status} /> },
      { name: "When", render: (r) => formatDateTime(r.eventat || r.createddate) },
      { name: "Details", align: "left", render: (r) => [r.errorcode && `Code ${r.errorcode}`, r.source === "POLL" ? "fetched" : "pushed"].filter(Boolean).join(" · ") },
    ]
    : [
      { name: "Email", accessor: "email", align: "left" },
      { name: "Candidate ID", accessor: "candidateid" },
      { name: "Latest Status", render: (r) => <StatusChip status={r.event} /> },
      { name: "When", render: (r) => formatDateTime(r.eventat) },
      { name: "Details", align: "left", render: (r) => r.reason || "—" },
    ];
  const deliveryFilters = isWhatsapp ? ["", "sent", "delivered", "read", "failed"]
    : isSms ? ["", "Delivered", "Pending", "Failed", "DND"] : ["", "delivered", "open", "bounce", "dropped", "deferred"];

  return (
    <Grid container rowSpacing={2}>
      <Grid item xs={12} sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {channelLabel(job.channel)} Job {job.jobid} · {job.templatename} <Typography component="span" variant="body2" sx={{ color: "#777" }}>({job.templatecode} v{job.version})</Typography>
          </Typography>
          <Typography variant="body2" sx={{ color: "#555" }}>
            {audienceText(job)} · started {formatDateTime(job.createddate)}{job.finisheddate ? ` · finished ${formatDateTime(job.finisheddate)}` : ""}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <StatusChip status={job.status} />
          <Button variant="outlined" size="small" onClick={() => navigate(`/${project}/send-history?page=list`)}>Back</Button>
        </Box>
      </Grid>
      {job.error && (
        <Grid item xs={12}>
          <Alert severity={job.status === "INTERRUPTED" ? "warning" : "error"}>{job.error}</Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <CountTile label="Rows in file" value={job.totalrows} />
        <CountTile label="Sent" value={job.sent} color="rgba(34,160,107,1)" />
        <CountTile label="Skipped" value={job.skipped} color="rgba(181,98,0,1)" />
        <CountTile label="Failed" value={job.failed} color={job.failed ? "rgba(200,40,30,1)" : undefined} />
        {job.status === "RUNNING" && <CountTile label="Still to send" value={Math.max(job.recipients - job.sent - job.failed, 0)} />}
      </Grid>
      {Object.keys(job.sendvalues || {}).length > 0 && (
        <Grid item xs={12}>
          <Typography variant="body2"><b>Values used:</b> {Object.entries(job.sendvalues).map(([k, v]) => `${k} = ${v}`).join(" · ")}</Typography>
        </Grid>
      )}
      {job.reasons?.length > 0 && (
        <Grid item xs={12}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Why rows were not sent</Typography>
          <EmailTable
            columns={[
              { name: "Status", render: (r) => <StatusChip status={r.status} /> },
              { name: "Reason", accessor: "reason", align: "left" },
              { name: "Rows", accessor: "count" },
            ]}
            rows={job.reasons}
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab value="rows" label="Rows" />
          {hasDelivery && <Tab value="delivery" label={isSms ? "Delivery (SMS gateway)" : isWhatsapp ? "Delivery (WhatsApp)" : "Delivery (SendGrid)"} />}
        </Tabs>
      </Grid>

      {tab === "rows" && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, flexWrap: "wrap", gap: 1 }}>
            <Tabs value={status} onChange={(e, v) => { setStatus(v); setRowsPage(1); }} sx={{ minHeight: 36 }}>
              {["", "SENT", "SKIPPED", "FAILED"].map((s) => <Tab key={s || "all"} value={s} label={s || "All"} sx={{ minHeight: 36 }} />)}
            </Tabs>
            <GreenButton title="Download Excel" handleSubmit={exportRows} />
          </Box>
          <EmailTable columns={rowColumns} rows={rows.rows} />
          {rows.total > PAGE_SIZE && <TablePagination pageCount={Math.ceil(rows.total / PAGE_SIZE)} currentPage={rowsPage} handlePageChange={(e, v) => setRowsPage(v)} />}
        </Grid>
      )}

      {hasDelivery && tab === "delivery" && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, mb: 1 }}>
            <Typography variant="body2" sx={{ color: "#555" }}>
              {isSms
                ? "The latest status the SMS gateway reported for each SMS of this job. Fetched every few minutes, or pushed by the gateway."
                : isWhatsapp
                  ? "The latest status WhatsApp reported for each message of this job (sent, delivered, read or failed, e.g. the number is not on WhatsApp). Pushed by the WhatsApp provider."
                  : "The latest status SendGrid reported for each email of this job (needs the SendGrid event webhook)."}
            </Typography>
            {isSms && <Button variant="outlined" size="small" onClick={fetchSmsDelivery}>Fetch now</Button>}
          </Box>
          <Tabs value={deliveryEvent} onChange={(e, v) => { setDeliveryEvent(v); setDeliveryPage(1); }} sx={{ minHeight: 36, mb: 1 }}>
            {deliveryFilters.map((s) => <Tab key={s || "all"} value={s} label={s || "All"} sx={{ minHeight: 36 }} />)}
          </Tabs>
          <EmailTable columns={deliveryColumns} rows={delivery.rows} emptyText="No delivery events yet" />
          {delivery.total > PAGE_SIZE && <TablePagination pageCount={Math.ceil(delivery.total / PAGE_SIZE)} currentPage={deliveryPage} handlePageChange={(e, v) => setDeliveryPage(v)} />}
        </Grid>
      )}
    </Grid>
  );
}
